import { useState, useEffect, useRef } from 'react';
import { Contract, SetAsideType } from '../types';
import { supabase, retryRequest } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { ContractMatcher } from '../lib/matching/ContractMatcher';
import { cacheContractScores } from '../lib/score-cache-service';
import { getPipelineStages } from '../lib/pipeline-service';
import toast from 'react-hot-toast';

/**
 * Overlay the AI capture-analyst verdict (ai_contract_matches) on top of the
 * deterministic scores. Where the AI has scored a (user, contract), its fit_score
 * SUPERSEDES matchScore and the tier/eligibility/reasoning are attached so the card
 * can show one badge ("AI") with the smart verdict. Contracts the AI hasn't reached
 * keep their deterministic score. Degrades gracefully (returns input unchanged) on
 * any error so scoring never breaks the contract list.
 */
async function overlayAiMatches(client: typeof supabase, userId: string, contracts: Contract[]): Promise<Contract[]> {
  if (!contracts.length) return contracts;
  try {
    const ids = contracts.map(c => c.id);
    const byContract = new Map<string, any>();
    // Chunk the IN() list so a large page doesn't exceed URL limits.
    for (let i = 0; i < ids.length; i += 200) {
      const chunk = ids.slice(i, i + 200);
      const { data } = await client
        .from('ai_contract_matches')
        .select('contract_id, fit_score, tier, eligibility, rationale, recommended_action, blockers, opportunities')
        .eq('user_id', userId)
        .in('contract_id', chunk);
      (data || []).forEach((m: any) => byContract.set(m.contract_id, m));
    }
    if (byContract.size === 0) return contracts;
    return contracts.map(c => {
      const m = byContract.get(c.id);
      if (!m) return c;
      return {
        ...c,
        // A 0 AI fit_score is a parse/stale default, not a real verdict — never let it
        // clobber a good deterministic/effective score. Only supersede when fit_score > 0.
        matchScore: typeof m.fit_score === 'number' && m.fit_score > 0 ? m.fit_score : c.matchScore,
        aiScored: typeof m.fit_score === 'number' && m.fit_score > 0,
        aiTier: m.tier ?? undefined,
        aiEligibility: m.eligibility ?? undefined,
        aiRationale: m.rationale ?? undefined,
        aiRecommendedAction: m.recommended_action ?? undefined,
        aiBlockers: Array.isArray(m.blockers) ? m.blockers : undefined,
        aiOpportunities: Array.isArray(m.opportunities) ? m.opportunities : undefined,
      };
    });
  } catch (e) {
    console.warn('AI match overlay skipped:', e);
    return contracts;
  }
}

interface ContractFilters {
  keywords?: string;
  naicsCode?: string[];
  status?: ('open' | 'pending' | 'closed')[];
  agency?: string[];
  minAmount?: number;
  maxAmount?: number;
  setAsides?: SetAsideType[];
  state?: string;
  region?: string;
  organizationType?: string;
  responseType?: string;
  active?: boolean;
  page?: number;
  limit?: number;
  favoritesOnly?: boolean;
  sortBy?: 'bidwin_score' | 'due_date' | 'posted_date' | 'funding_amount' | 'agency';
  sortOrder?: 'asc' | 'desc';
  excludePastDue?: boolean;
  pipelineStage?: 'watching' | 'pursuing' | 'submitted' | 'won' | 'lost' | 'skipped';
  dueWithinDays?: number;
}

// Helper to apply filters to a Supabase query
function applyFiltersToQuery(query: any, filters: ContractFilters) {
  // Keyword search - uses PostgreSQL ILIKE for case-insensitive pattern matching
  // Searches across: title, description, agency, naics_code
  // NOTE: This is basic text search (not vector/semantic search)
  // For semantic search, would need to integrate embeddings (e.g., pgvector)
  if (filters.keywords && filters.keywords.trim()) {
    const searchTerm = filters.keywords.trim().replace(/'/g, "''");
    query = query.or(
      `title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,agency.ilike.%${searchTerm}%,naics_code.ilike.%${searchTerm}%`
    );
  }

  // Status filter
  if (filters.status && filters.status.length > 0) {
    query = query.in('status', filters.status);
  }

  // NAICS code filter
  if (filters.naicsCode && filters.naicsCode.length > 0) {
    // Check both naics_code and naics_codes array
    query = query.or(
      `naics_code.in.(${filters.naicsCode.join(',')}),naics_codes.ov.{${filters.naicsCode.join(',')}}`
    );
  }

  // Agency filter
  if (filters.agency && filters.agency.length > 0) {
    query = query.in('agency', filters.agency);
  }

  // Set-asides filter (array overlap)
  if (filters.setAsides && filters.setAsides.length > 0) {
    query = query.overlaps('set_asides', filters.setAsides);
  }

  // Funding amount range
  if (filters.minAmount !== undefined && filters.minAmount > 0) {
    query = query.gte('funding_amount', filters.minAmount);
  }
  if (filters.maxAmount !== undefined && filters.maxAmount < 10000000) {
    query = query.lte('funding_amount', filters.maxAmount);
  }

  // State/location filter (place_of_performance is JSONB)
  if (filters.state && filters.state.trim()) {
    // Handle both string and object formats for state
    const stateValue = filters.state.trim();
    query = query.or(
      `place_of_performance->>state.ilike.%${stateValue}%,place_of_performance->state->>name.ilike.%${stateValue}%`
    );
  }

  // Organization type filter
  if (filters.organizationType) {
    query = query.eq('organization_type', filters.organizationType);
  }

  // Response type filter
  if (filters.responseType) {
    query = query.eq('response_type', filters.responseType);
  }

  // Active filter (default to true if not specified)
  if (filters.active !== false) {
    query = query.eq('active', true);
  }

  // Exclude archived contracts
  query = query.eq('archived', false);

  // Exclude past due contracts if requested
  if (filters.excludePastDue) {
    const today = new Date().toISOString().split('T')[0];
    query = query.gte('due_date', today);
  }

  return query;
}

// Select columns for sam_contracts query
export const SELECT_COLUMNS = `
  notice_id,
  title,
  naics_code,
  naics_codes,
  agency,
  due_date,
  posted_date,
  funding_amount,
  description,
  status,
  type,
  set_asides,
  place_of_performance,
  contracting_officer,
  point_of_contact,
  solicitation_url,
  additional_info_link,
  ui_link,
  active,
  archived,
  resource_links,
  metadata,
  classification_code,
  psc_code,
  award_number,
  award_date,
  award_amount,
  base_period_amount,
  total_contract_value,
  option_periods,
  organization_type,
  office_address,
  is_recompete,
  incumbent_contractor,
  total_bidders,
  estimated_award_date,
  period_of_performance,
  clearance_required,
  subcontracting_goals,
  required_certifications,
  required_experience,
  evaluation_criteria,
  response_type,
  response_instructions,
  questions_deadline,
  questions_allowed
`.replace(/\s+/g, '');

// Transform a database record to Contract type
export function transformContract(record: any): Contract {
  const metadata = record.metadata || {};
  const rawAttachments = metadata.attachments || [];
  const attachments = Array.isArray(rawAttachments)
    ? rawAttachments.map((att: any) => ({
        name: att.name || att.fileName || att.title || 'Document',
        url: att.url || att.link || att.accessUrl || null,
        type: att.type || att.mimeType || att.fileType || 'application/pdf',
        size: att.size || att.fileSize || null,
        datePosted: att.postedDate || att.datePosted || att.uploadDate || null
      })).filter((att: any) => att.url)
    : [];

  const resourceLinks = record.resource_links || [];

  return {
    id: record.notice_id,
    notice_id: record.notice_id,
    title: record.title,
    description: record.description,
    agency: record.agency,
    status: record.status,
    type: record.type,
    classificationCode: record.classification_code,
    pscCode: record.psc_code,
    naicsCode: record.naics_code,
    naicsCodes: record.naics_codes,
    setAsides: record.set_asides,
    dueDate: record.due_date,
    postedDate: record.posted_date,
    estimatedAwardDate: record.estimated_award_date,
    questionsDeadline: record.questions_deadline,
    awardDate: record.award_date,
    fundingAmount: record.funding_amount,
    awardAmount: record.award_amount,
    awardNumber: record.award_number,
    basePeriodAmount: record.base_period_amount,
    totalContractValue: record.total_contract_value,
    optionPeriods: record.option_periods,
    placeOfPerformance: record.place_of_performance,
    officeAddress: record.office_address,
    organizationType: record.organization_type,
    contractingOfficer: record.contracting_officer,
    pointOfContact: record.point_of_contact,
    isRecompete: record.is_recompete,
    incumbentContractor: record.incumbent_contractor,
    totalBidders: record.total_bidders,
    clearanceRequired: record.clearance_required,
    periodOfPerformance: record.period_of_performance,
    responseType: record.response_type,
    responseInstructions: record.response_instructions,
    questionsAllowed: record.questions_allowed,
    requiredCertifications: record.required_certifications,
    requiredExperience: record.required_experience,
    evaluationCriteria: record.evaluation_criteria,
    subcontractingGoals: record.subcontracting_goals,
    solicitationUrl: record.solicitation_url,
    additionalInfoLink: record.additional_info_link,
    uiLink: record.ui_link,
    active: record.active,
    archived: record.archived,
    resourceLinks: resourceLinks,
    attachments: attachments,
    metadata: record.metadata,
    hasDocuments: attachments.length > 0 || resourceLinks.length > 0,
    matchScore: 0,
    isFavorited: false,
    notifications: [],
    // Legacy snake_case aliases. Several views (ContractSlideout, ContractDetails) still
    // read these names; without them the incumbent alert, point-of-contact, office address,
    // and Q&A-deadline sections silently never rendered on transformed contracts.
    point_of_contact: record.point_of_contact,
    office_address: record.office_address,
    is_recompete: record.is_recompete,
    incumbent_contractor: record.incumbent_contractor,
    questions_deadline: record.questions_deadline
  };
}

export function useContracts(filters: ContractFilters = {}, refreshTrigger?: number) {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const { user, profile } = useAuth();

  // Track if we're currently caching scores to avoid duplicate work
  const isCachingRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    async function fetchContracts() {
      try {
        setLoading(true);
        setError(null);

        const freshClient = supabase;
        const pageSize = filters.limit || 10;
        const pageOffset = (filters.page || 0) * pageSize;
        const sortBy = filters.sortBy || 'due_date';
        const sortOrder = filters.sortOrder || 'asc';

        // ── UNIFIED AI-AWARE PATH ───────────────────────────────────────────────
        // One DB function (list_contracts_ranked) filters + sorts + paginates on an
        // effective_score = COALESCE(valid AI fit_score, deterministic bidwin_score).
        // This fixes: order matching the displayed score, AI scores actually driving
        // ranking, filters applying consistently, and correct totals/pagination —
        // the bugs the two legacy paths below caused. Falls through to the legacy
        // paths only if the RPC errors.
        if (user) {
          try {
            const sortKey = sortBy === 'bidwin_score' ? 'effective_score' : sortBy;
            const { data: ranked, error: rankErr } = await freshClient.rpc('list_contracts_ranked', {
              p_user_id: user.id,
              p_keywords: filters.keywords?.trim() || null,
              p_status: filters.status && filters.status.length ? filters.status : null,
              p_naics: filters.naicsCode && filters.naicsCode.length ? filters.naicsCode : null,
              p_agency: filters.agency && filters.agency.length ? filters.agency : null,
              p_set_asides: filters.setAsides && filters.setAsides.length ? filters.setAsides : null,
              p_min_amount: filters.minAmount && filters.minAmount > 0 ? filters.minAmount : null,
              p_max_amount: filters.maxAmount !== undefined && filters.maxAmount < 10000000 ? filters.maxAmount : null,
              p_state: filters.state?.trim() || null,
              p_organization_type: filters.organizationType || null,
              p_response_type: filters.responseType || null,
              p_active: filters.active !== false,
              p_exclude_past_due: !!filters.excludePastDue,
              p_favorites_only: !!filters.favoritesOnly,
              p_pipeline_stage: filters.pipelineStage || null,
              p_due_within_days: filters.dueWithinDays ?? null,
              p_sort: sortKey,
              p_desc: sortOrder !== 'asc',
              p_limit: pageSize,
              p_offset: pageOffset,
            });
            if (rankErr) throw rankErr;

            let rankedContracts: Contract[] = [];
            const rankedTotal = ranked && ranked.length ? Number((ranked[0] as any).total_count) || ranked.length : 0;

            if (ranked && ranked.length > 0) {
              const orderedIds = ranked.map((r: any) => r.notice_id);
              const scoreById = new Map(ranked.map((r: any) => [r.notice_id, r]));
              const { data: rows, error: rowsErr } = await freshClient
                .from('sam_contracts').select(SELECT_COLUMNS).in('notice_id', orderedIds);
              if (rowsErr) throw rowsErr;
              const rowMap = new Map((rows || []).map((c: any) => [c.notice_id, c]));
              rankedContracts = orderedIds
                .map((id: string) => rowMap.get(id))
                .filter(Boolean)
                .map((record: any) => {
                  const contract = transformContract(record);
                  const r: any = scoreById.get(record.notice_id);
                  contract.matchScore = Math.round(Number(r?.effective_score) || 0);
                  return contract;
                });

              // Favorites + notifications + AI detail overlay (rationale/tier/etc.).
              const [favoritesResponse, notificationsResponse] = await Promise.all([
                retryRequest(() => freshClient.from('contract_favorites').select('contract_id').eq('user_id', user.id)),
                retryRequest(() => freshClient.from('contract_notifications').select('contract_id, type, frequency').eq('user_id', user.id)),
              ]);
              const favoriteIds = new Set(favoritesResponse.data?.map((f: any) => f.contract_id) || []);
              const notifByContract = (notificationsResponse.data || []).reduce((acc: any, n: any) => {
                (acc[n.contract_id] ||= []).push({ type: n.type, frequency: n.frequency }); return acc;
              }, {} as Record<string, { type: string; frequency: string }[]>);
              const pipelineStages = await getPipelineStages(user.id, rankedContracts.map(c => c.id));
              rankedContracts = rankedContracts.map(c => ({
                ...c,
                isFavorited: favoriteIds.has(c.id),
                notifications: notifByContract[c.id] || [],
                pipelineStage: pipelineStages.get(c.id),
              }));
              // Attach AI detail fields; the 0-guard keeps it from clobbering matchScore.
              rankedContracts = await overlayAiMatches(freshClient, user.id, rankedContracts);

              // Cold-start: a contract with no cached deterministic score and no AI verdict
              // comes back as 0. Compute client-side for display and warm the cache so the
              // next load ranks it correctly.
              const uncached = rankedContracts.filter(c => !c.aiScored && (!c.matchScore || c.matchScore === 0));
              if (uncached.length > 0 && profile) {
                rankedContracts = rankedContracts.map(c =>
                  (!c.aiScored && !c.matchScore) ? { ...c, matchScore: ContractMatcher.calculateMatchScore(c, profile) } : c
                );
                if (!isCachingRef.current) {
                  isCachingRef.current = true;
                  cacheContractScores(user.id, uncached, profile)
                    .catch(err => console.error('Failed to cache scores:', err))
                    .finally(() => { isCachingRef.current = false; });
                }
              }
            }

            if (mounted) {
              setContracts(rankedContracts);
              setTotal(rankedTotal);
              setError(null);
            }
            return;
          } catch (rpcErr) {
            console.warn('Ranked RPC failed; falling back to legacy paths:', rpcErr);
          }
        }

        // When sorting by BidWin score, we need a different strategy:
        // Query the cached scores table to get globally sorted results.
        // BUT only a subset of contracts have cached scores, so when the user
        // is searching by keyword we must fall through to the full-table path
        // below — otherwise search only covers the pre-scored subset and most
        // matches (un-scored contracts) silently return nothing.
        const hasKeywordSearch = !!(filters.keywords && filters.keywords.trim());
        if (sortBy === 'bidwin_score' && user && !hasKeywordSearch) {
          // STEP 1: Get contract IDs sorted by cached BidWin score
          // Join with sam_contracts to apply filters, but sort by score
          let scoreQuery = freshClient
            .from('user_contract_scores')
            .select(`
              contract_id,
              bidwin_score,
              sam_contracts!inner (
                notice_id,
                status,
                active,
                archived,
                due_date,
                naics_code,
                naics_codes,
                agency,
                funding_amount,
                set_asides,
                place_of_performance,
                organization_type,
                response_type,
                title,
                description
              )
            `)
            .eq('user_id', user.id);

          // Apply filters to the joined sam_contracts
          // Status filter
          if (filters.status && filters.status.length > 0) {
            scoreQuery = scoreQuery.in('sam_contracts.status', filters.status);
          }

          // Active filter
          if (filters.active !== false) {
            scoreQuery = scoreQuery.eq('sam_contracts.active', true);
          }

          // Archived filter
          scoreQuery = scoreQuery.eq('sam_contracts.archived', false);

          // Exclude past due
          if (filters.excludePastDue) {
            const today = new Date().toISOString().split('T')[0];
            scoreQuery = scoreQuery.gte('sam_contracts.due_date', today);
          }

          // NAICS filter
          if (filters.naicsCode && filters.naicsCode.length > 0) {
            scoreQuery = scoreQuery.or(
              `sam_contracts.naics_code.in.(${filters.naicsCode.join(',')}),sam_contracts.naics_codes.ov.{${filters.naicsCode.join(',')}}`
            );
          }

          // Agency filter
          if (filters.agency && filters.agency.length > 0) {
            scoreQuery = scoreQuery.in('sam_contracts.agency', filters.agency);
          }

          // Set-asides filter
          if (filters.setAsides && filters.setAsides.length > 0) {
            scoreQuery = scoreQuery.overlaps('sam_contracts.set_asides', filters.setAsides);
          }

          // Funding amount range
          if (filters.minAmount !== undefined && filters.minAmount > 0) {
            scoreQuery = scoreQuery.gte('sam_contracts.funding_amount', filters.minAmount);
          }
          if (filters.maxAmount !== undefined && filters.maxAmount < 10000000) {
            scoreQuery = scoreQuery.lte('sam_contracts.funding_amount', filters.maxAmount);
          }

          // Keyword search on joined table
          if (filters.keywords && filters.keywords.trim()) {
            const searchTerm = filters.keywords.trim().replace(/'/g, "''");
            scoreQuery = scoreQuery.or(
              `sam_contracts.title.ilike.%${searchTerm}%,sam_contracts.description.ilike.%${searchTerm}%,sam_contracts.agency.ilike.%${searchTerm}%,sam_contracts.naics_code.ilike.%${searchTerm}%`
            );
          }

          // Sort by score (and due_date as secondary)
          scoreQuery = scoreQuery
            .order('bidwin_score', { ascending: sortOrder === 'asc' })
            .order('sam_contracts(due_date)', { ascending: true });

          // Get total count first
          const { count: scoreCount } = await freshClient
            .from('user_contract_scores')
            .select('contract_id', { count: 'exact', head: true })
            .eq('user_id', user.id);

          // Apply pagination
          scoreQuery = scoreQuery.range(pageOffset, pageOffset + pageSize - 1);

          const { data: scoreData, error: scoreError } = await scoreQuery;

          if (scoreError) {
            console.warn('Error fetching from score cache, falling back to standard query:', scoreError);
            // Fall through to standard query below
          } else if (scoreData && scoreData.length > 0) {
            // Extract contract IDs in score-sorted order
            const sortedContractIds = scoreData.map((s: any) => s.contract_id);
            const scoreMap = new Map(scoreData.map((s: any) => [s.contract_id, s.bidwin_score]));

            // STEP 2: Fetch full contract details for these specific IDs
            const { data: contractsData, error: contractsError } = await freshClient
              .from('sam_contracts')
              .select(SELECT_COLUMNS)
              .in('notice_id', sortedContractIds);

            if (contractsError) throw contractsError;

            // STEP 3: Transform and maintain the score-sorted order
            const contractsMap = new Map(
              (contractsData || []).map(c => [c.notice_id, c])
            );

            let transformedContracts: Contract[] = sortedContractIds
              .map(id => contractsMap.get(id))
              .filter(Boolean)
              .map((record: any) => {
                const contract = transformContract(record);
                contract.matchScore = scoreMap.get(record.notice_id) || 0;
                return contract;
              });

            // STEP 4: Add favorites and notifications
            if (profile && mounted) {
              const [favoritesResponse, notificationsResponse] = await Promise.all([
                retryRequest(() =>
                  freshClient
                    .from('contract_favorites')
                    .select('contract_id')
                    .eq('user_id', user.id)
                ),
                retryRequest(() =>
                  freshClient
                    .from('contract_notifications')
                    .select('contract_id, type, frequency')
                    .eq('user_id', user.id)
                )
              ]);

              const favoriteIds = new Set(
                favoritesResponse.data?.map(f => f.contract_id) || []
              );

              const notificationsByContract = (notificationsResponse.data || []).reduce((acc, n) => {
                if (!acc[n.contract_id]) acc[n.contract_id] = [];
                acc[n.contract_id].push({ type: n.type, frequency: n.frequency });
                return acc;
              }, {} as Record<string, { type: string; frequency: string }[]>);

              transformedContracts = transformedContracts.map(contract => ({
                ...contract,
                isFavorited: favoriteIds.has(contract.id),
                notifications: notificationsByContract[contract.id] || []
              }));

              if (filters.favoritesOnly) {
                transformedContracts = transformedContracts.filter(c => c.isFavorited);
              }
            }

            // Overlay AI verdicts so cards show the smart score even in the
            // score-sorted path (display supersede; DB ordering kept).
            transformedContracts = await overlayAiMatches(freshClient, user.id, transformedContracts);

            if (mounted) {
              setContracts(transformedContracts);
              // Use the filtered count from the score query, fallback to scoreCount
              setTotal(scoreCount || transformedContracts.length);
              setError(null);
            }
            return; // Successfully used score-sorted path
          }
        }

        // STANDARD PATH: For non-score sorting or when score cache is unavailable
        // STEP 1: Get the total count of filtered contracts from sam_contracts
        let countQuery = freshClient
          .from('sam_contracts')
          .select('notice_id', { count: 'exact', head: true });
        countQuery = applyFiltersToQuery(countQuery, filters);

        const { count: totalCount, error: countError } = await countQuery;
        if (countError) {
          console.warn('Error getting contract count:', countError);
        }

        // STEP 2: Fetch contracts with filters and pagination
        let query = freshClient
          .from('sam_contracts')
          .select(SELECT_COLUMNS);

        // Apply all filters
        query = applyFiltersToQuery(query, filters);

        // Apply sorting (bidwin_score falls back to due_date since it's calculated client-side)
        if (sortBy !== 'bidwin_score') {
          query = query.order(sortBy, { ascending: sortOrder === 'asc' });
        } else {
          // Fallback: sort by due_date, will re-sort by score client-side
          query = query.order('due_date', { ascending: true });
        }

        // Apply pagination
        query = query.range(pageOffset, pageOffset + pageSize - 1);

        const { data: contractsData, error: storageError } = await retryRequest(() => query);

        if (storageError) throw storageError;

        let transformedContracts = (contractsData || []).map(transformContract);

        // STEP 3: Enhance with user data (favorites, notifications, scores)
        if (user && profile && mounted) {
          // Fetch favorites and notifications in parallel
          const [favoritesResponse, notificationsResponse] = await Promise.all([
            retryRequest(() =>
              freshClient
                .from('contract_favorites')
                .select('contract_id')
                .eq('user_id', user.id)
            ),
            retryRequest(() =>
              freshClient
                .from('contract_notifications')
                .select('contract_id, type, frequency')
                .eq('user_id', user.id)
            )
          ]);

          const favoriteIds = new Set(
            favoritesResponse.data?.map(f => f.contract_id) || []
          );

          const notificationsByContract = (notificationsResponse.data || []).reduce((acc, n) => {
            if (!acc[n.contract_id]) acc[n.contract_id] = [];
            acc[n.contract_id].push({ type: n.type, frequency: n.frequency });
            return acc;
          }, {} as Record<string, { type: string; frequency: string }[]>);

          // Try to get cached scores for these contracts
          const contractIds = transformedContracts.map(c => c.id);
          let scoreMap = new Map<string, number>();

          try {
            const { data: cachedScores } = await freshClient
              .from('user_contract_scores')
              .select('contract_id, bidwin_score')
              .eq('user_id', user.id)
              .in('contract_id', contractIds);

            if (cachedScores) {
              scoreMap = new Map(cachedScores.map(s => [s.contract_id, s.bidwin_score]));
            }
          } catch (e) {
            // Score cache might not exist, continue without it
          }

          // Calculate scores for contracts without cached scores
          transformedContracts = transformedContracts.map(contract => {
            const cachedScore = scoreMap.get(contract.id);
            const matchScore = cachedScore !== undefined
              ? cachedScore
              : ContractMatcher.calculateMatchScore(contract, profile);

            return {
              ...contract,
              matchScore,
              isFavorited: favoriteIds.has(contract.id),
              notifications: notificationsByContract[contract.id] || []
            };
          });

          // Overlay AI verdicts (supersede matchScore where the AI has scored) BEFORE
          // sorting, so the order reflects the smart score where available.
          transformedContracts = await overlayAiMatches(freshClient, user.id, transformedContracts);

          // If sorting by BidWin score (fallback path), sort client-side
          if (sortBy === 'bidwin_score') {
            transformedContracts.sort((a, b) => {
              const scoreDiff = sortOrder === 'desc'
                ? b.matchScore - a.matchScore
                : a.matchScore - b.matchScore;
              if (scoreDiff !== 0) return scoreDiff;

              // Secondary sort by due date
              const daysA = a.dueDate ? Math.ceil((new Date(a.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 9999;
              const daysB = b.dueDate ? Math.ceil((new Date(b.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : 9999;
              return daysA - daysB;
            });
          }

          // Cache scores in the background for contracts that didn't have cached scores
          const uncachedContracts = transformedContracts.filter(c => !scoreMap.has(c.id));
          if (!isCachingRef.current && uncachedContracts.length > 0) {
            isCachingRef.current = true;
            cacheContractScores(user.id, uncachedContracts, profile)
              .catch(err => console.error('Failed to cache scores:', err))
              .finally(() => { isCachingRef.current = false; });
          }

          // Filter favorites if requested
          if (filters.favoritesOnly) {
            transformedContracts = transformedContracts.filter(c => c.isFavorited);
          }
        }

        if (mounted) {
          setContracts(transformedContracts);
          setTotal(totalCount || transformedContracts.length);
          setError(null);
        }
      } catch (error) {
        console.error('Error fetching contracts:', error);
        if (mounted) {
          setError(error instanceof Error ? error : new Error('Failed to fetch contracts'));
          toast.error('Failed to load contracts. Please try again.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchContracts();

    return () => {
      mounted = false;
    };
  }, [filters, user, profile, refreshTrigger, refreshTick]);

  return {
    contracts,
    loading,
    error,
    total,
    // Re-run the fetch (bumps an internal trigger so the effect actually re-fires).
    refresh: () => {
      setError(null);
      setRefreshTick(n => n + 1);
    }
  };
}